import type { QueryDetailsTableViewProps } from "@/domains/diagnostics/types"
import type { Sort } from "@/domains/table-processor"
import { Button } from "@/components/button"
import { SortableTableHeader } from "@/components/sortable-table-header"
import { Compound as Table } from "@/components/table"
import { Lozenge } from "@/domains/common/components/lozenge"

import { ChunkSizeSliderView } from "./chunk-size-slider-view"

export const QueryDetailsTableView = ({
  queries,
  onStopQuery,
  onForceExecuteQuery,
  sortBy,
  setSortBy,
}: QueryDetailsTableViewProps) => {
  const handleSort = (key: string, type: "string" | "number") => {
    if (!setSortBy) return

    setSortBy({
      key,
      direction: sortBy?.key === key && sortBy?.direction === "asc" ? "desc" : "asc",
      type,
    })
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-t100">Individual Query Details</h3>
      <Table.Root>
        <Table.Head>
          <Table.HeaderRow>
            <SortableTableHeader
              sortBy={sortBy ?? ({ key: "", direction: "asc", type: "string" } as Sort)}
              sortColumn="name"
              onClick={() => handleSort("name", "string")}
            >
              Query Name
            </SortableTableHeader>
            <SortableTableHeader
              sortBy={sortBy ?? ({ key: "", direction: "asc", type: "string" } as Sort)}
              sortColumn="uniqueId"
              onClick={() => handleSort("uniqueId", "string")}
            >
              ID
            </SortableTableHeader>
            <SortableTableHeader
              sortBy={sortBy ?? ({ key: "", direction: "asc", type: "string" } as Sort)}
              sortColumn="status"
              onClick={() => handleSort("status", "string")}
            >
              Status
            </SortableTableHeader>
            <Table.Th isSortDisabled>Interval</Table.Th>
            <Table.Th isSortDisabled>Last Execution</Table.Th>
            <Table.Th isSortDisabled>Performance</Table.Th>
            <Table.Th isSortDisabled>Chunk Size</Table.Th>
            <Table.Th isSortDisabled>Actions</Table.Th>
          </Table.HeaderRow>
        </Table.Head>
        <Table.Body>
          {queries.map((query) => (
            <Table.Tr key={`${query.name}-${query.uniqueId}`}>
              <Table.Td>
                <div className="space-y-1">
                  <div className="font-medium text-t100">{query.name}</div>
                  {query.rpcMetrics && (
                    <div className="flex flex-wrap items-center gap-1">
                      <Lozenge color="blue" size="xxs">
                        {query.rpcMetrics.totalRpcCalls} RPC
                      </Lozenge>
                      <Lozenge color="blue" size="xxs">
                        {query.rpcMetrics.totalChunks} chunks
                      </Lozenge>
                      {query.rpcMetrics.chunkSize > 0 && (
                        <Lozenge color="blue" size="xxs">
                          chunk: {query.rpcMetrics.chunkSize}
                        </Lozenge>
                      )}
                      {query.rpcMetrics.averageBytesPerItem > 0 && (
                        <Lozenge color="success" size="xxs">
                          {Math.round(query.rpcMetrics.averageBytesPerItem)}B/item
                        </Lozenge>
                      )}
                      {query.rpcMetrics.failedRpcCalls > 0 && (
                        <Lozenge color="error" size="xxs">
                          {query.rpcMetrics.failedRpcCalls} failed
                        </Lozenge>
                      )}
                    </div>
                  )}
                </div>
              </Table.Td>
              <Table.Td>
                <div className="font-mono text-xs text-t200" title={query.uniqueId}>
                  {query.uniqueIdShort}
                </div>
              </Table.Td>
              <Table.Td>
                <div className="flex items-center gap-2">
                  <div
                    className={`h-3 w-3 rounded-full ${query.status === "active" ? "bg-green-400" : "bg-gray-400"}`}
                  ></div>
                  <span className="text-sm text-t200">{query.status === "active" ? "Active" : "Inactive"}</span>
                </div>
              </Table.Td>
              <Table.Td>
                <div className="text-sm text-t200">{query.interval}</div>
              </Table.Td>
              <Table.Td>
                <div className="text-sm text-t200">{query.lastExecution}</div>
              </Table.Td>
              <Table.Td>
                <div className="text-sm text-t200">
                  Count: {query.executionCount}, Avg: {query.averageExecutionTime}
                  {query.lastExecutionDuration && `, Last: ${query.lastExecutionDuration}`}
                </div>
              </Table.Td>
              <Table.Td>
                {query.query.getChunkSizeBounds() ? (
                  <ChunkSizeSliderView
                    min={query.query.getChunkSizeBounds()!.min}
                    max={query.query.getChunkSizeBounds()!.max}
                    value={query.query.chunkSize}
                    onChange={(size) => query.query.setChunkSize(size)}
                    disabled={query.isRunning}
                  />
                ) : (
                  <span className="text-xs text-t300">N/A</span>
                )}
              </Table.Td>
              <Table.Td>
                <div className="flex gap-2">
                  <Button variant="neutral" size="sm" onClick={() => onStopQuery(query.query)}>
                    Stop
                  </Button>
                  <Button variant="primary" size="sm" onClick={() => onForceExecuteQuery(query.query)}>
                    Force
                  </Button>
                </div>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Body>
      </Table.Root>
    </div>
  )
}
